import { useEffect, useId, useRef, useState } from 'react';
import { memberApi } from '@formosa/shared/api/member';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { memberUploadApi } from '../../api/memberUploadApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import SafeImage from '../../components/SafeImage';
import { Alert, Button, ConfirmDialog } from '../../components/ui';
import { buildProfileUpdatePayload } from '../creator-connections/creatorConnectionsModel';

const NICKNAME_MAX = 50;

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusableElements(container) {
  if (!container) return [];
  return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((node) => {
    if (!(node instanceof HTMLElement)) return false;
    if (node.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  });
}

function safeFocus(element) {
  if (!(element instanceof HTMLElement) || !element.isConnected) return;
  try {
    element.focus();
  } catch {
    /* ignore */
  }
}

/**
 * 本人個人資料編輯對話框：僅 nickname + avatar objectKey（既有 PUT /profile）。
 */
export default function CreatorProfileEditDialog({
  open,
  initialNickname = '',
  initialAvatarKey = '',
  initialAvatarUrl = '',
  onClose,
  onSaved,
}) {
  const { t } = useTravelTranslation();
  const titleId = useId();
  const leadId = useId();
  const nicknameId = useId();
  const fileId = useId();
  const panelRef = useRef(null);
  const nicknameRef = useRef(null);
  const priorFocusedRef = useRef(null);
  const busyRef = useRef(false);
  const dirtyRef = useRef(false);
  const onCloseRef = useRef(onClose);

  const [nickname, setNickname] = useState('');
  const [nicknameDirty, setNicknameDirty] = useState(false);
  const [avatarKey, setAvatarKey] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarDirty, setAvatarDirty] = useState(false);
  const [avatarCleared, setAvatarCleared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [discardOpen, setDiscardOpen] = useState(false);
  const discardOpenRef = useRef(false);

  const isDirty = nicknameDirty || avatarDirty;
  dirtyRef.current = isDirty;
  busyRef.current = busy || uploading;
  discardOpenRef.current = discardOpen;
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    setNickname(initialNickname == null ? '' : String(initialNickname));
    setNicknameDirty(false);
    setAvatarKey(initialAvatarKey || null);
    setAvatarPreview(initialAvatarUrl || (initialAvatarKey ? resolveMemberMediaUrl(initialAvatarKey) : ''));
    setAvatarDirty(false);
    setAvatarCleared(false);
    setBusy(false);
    setUploading(false);
    setError('');
    setStatus('');
    setDiscardOpen(false);
  }, [open, initialNickname, initialAvatarKey, initialAvatarUrl]);

  // open lifecycle：focus／Escape／Tab trap／restore
  useEffect(() => {
    if (!open) return undefined;
    priorFocusedRef.current = document.activeElement;
    const focusInitial = () => {
      safeFocus(nicknameRef.current);
      if (document.activeElement !== nicknameRef.current) {
        safeFocus(panelRef.current);
      }
    };
    focusInitial();

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        // 未儲存確認層開啟時交給 ConfirmDialog 處理 Escape
        if (busyRef.current || discardOpenRef.current) return;
        event.preventDefault();
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const root = panelRef.current;
      if (!root) return;
      const focusables = getFocusableElements(root);
      if (focusables.length === 0) {
        event.preventDefault();
        safeFocus(root);
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (event.shiftKey) {
        if (active === first || !root.contains(active)) {
          event.preventDefault();
          safeFocus(last);
        }
      } else if (active === last || !root.contains(active)) {
        event.preventDefault();
        safeFocus(first);
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const prior = priorFocusedRef.current;
      priorFocusedRef.current = null;
      if (prior instanceof HTMLElement && prior.isConnected) {
        safeFocus(prior);
      }
    };
  }, [open]);

  function requestClose() {
    if (busyRef.current) return;
    if (dirtyRef.current) {
      setDiscardOpen(true);
      return;
    }
    onCloseRef.current?.();
  }

  if (!open) return null;

  async function onPickFile(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    setStatus('');
    try {
      const objectKey = await memberUploadApi.uploadImage(file);
      const key = String(objectKey || '').trim();
      if (!key || key.includes('://') || !key.startsWith('image/member/')) {
        throw new Error('invalid-object-key');
      }
      setAvatarKey(key);
      setAvatarPreview(resolveMemberMediaUrl(key));
      setAvatarCleared(false);
      setAvatarDirty(true);
    } catch (cause) {
      setError(resolveSafeUserVisibleError(cause, t, 'travel.creator.avatarUploadFailed'));
    } finally {
      setUploading(false);
    }
  }

  function removeAvatar() {
    setAvatarKey(null);
    setAvatarPreview('');
    setAvatarCleared(true);
    setAvatarDirty(true);
    setError('');
    setStatus('');
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (busy || uploading) return;
    if (!nicknameDirty && !avatarDirty) {
      onClose?.();
      return;
    }
    if (nicknameDirty && nickname.trim().length > NICKNAME_MAX) {
      setError(t('travel.creator.nicknameTooLong'));
      return;
    }
    setBusy(true);
    setError('');
    setStatus('');
    try {
      const body = buildProfileUpdatePayload({
        nicknameDirty,
        nickname,
        avatarDirty,
        avatarObjectKey: avatarKey,
        avatarCleared,
      });
      const response = await memberApi.updateProfile(body);
      setNicknameDirty(false);
      setAvatarDirty(false);
      setStatus(t('travel.creator.profileSaved'));
      await onSaved?.(response, body);
      onClose?.();
    } catch (cause) {
      setError(resolveSafeUserVisibleError(cause, t, 'travel.creator.profileSaveFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="creator-edit-dialog" role="presentation">
      <button
        type="button"
        className="creator-edit-dialog__backdrop"
        aria-label={t('travel.creator.closeEdit')}
        data-testid="creator-profile-edit-backdrop"
        onClick={requestClose}
      />
      <div
        ref={panelRef}
        className="creator-edit-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={leadId}
        tabIndex={-1}
        data-testid="creator-profile-edit-dialog"
      >
        <header className="creator-edit-dialog__header">
          <h2 id={titleId}>{t('travel.creator.editTitle')}</h2>
          <p id={leadId} className="creator-edit-dialog__lead">
            {t('travel.creator.editLead')}
          </p>
        </header>
        <form className="creator-edit-dialog__form" onSubmit={onSubmit} noValidate>
          <div className="creator-edit-dialog__avatar">
            <SafeImage
              src={avatarPreview}
              alt=""
              fallback={(
                <span className="creator-edit-dialog__avatar-fallback" aria-hidden="true">
                  {(nickname.trim().slice(0, 1) || '?').toUpperCase()}
                </span>
              )}
            />
            <div className="creator-edit-dialog__avatar-actions">
              <label
                htmlFor={fileId}
                className={`creator-edit-dialog__file-label${(busy || uploading) ? ' is-disabled' : ''}`}
              >
                {uploading ? t('travel.creator.avatarUploading') : t('travel.creator.avatarUpload')}
              </label>
              <input
                id={fileId}
                type="file"
                accept="image/*"
                hidden
                disabled={busy || uploading}
                onChange={onPickFile}
              />
              <Button
                type="button"
                variant="secondary"
                disabled={busy || uploading || (!avatarPreview && !avatarKey)}
                onClick={removeAvatar}
              >
                {t('travel.creator.avatarRemove')}
              </Button>
            </div>
          </div>

          <label className="creator-edit-dialog__field" htmlFor={nicknameId}>
            <span>{t('travel.creator.nicknameLabel')}</span>
            <input
              ref={nicknameRef}
              id={nicknameId}
              type="text"
              maxLength={NICKNAME_MAX}
              value={nickname}
              disabled={busy}
              onChange={(e) => {
                setNickname(e.target.value);
                setNicknameDirty(true);
                setStatus('');
              }}
              autoComplete="nickname"
            />
          </label>
          <p className="creator-edit-dialog__hint">{t('travel.creator.nicknameHint')}</p>

          {error ? (
            <Alert variant="error" className="creator-edit-dialog__alert" title={error} />
          ) : null}
          {status ? (
            <p className="creator-edit-dialog__status" role="status">{status}</p>
          ) : null}

          <div className="creator-edit-dialog__actions">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              data-testid="creator-profile-edit-cancel"
              onClick={requestClose}
            >
              {t('travel.common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={busy} disabled={uploading}>
              {busy ? t('travel.creator.saving') : t('travel.creator.saveProfile')}
            </Button>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title={t('travel.creator.discardTitle')}
        description={t('travel.creator.discardBody')}
        confirmLabel={t('travel.creator.discardConfirm')}
        cancelLabel={t('travel.common.cancel')}
        danger
        onCancel={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose?.();
        }}
      />
    </div>
  );
}
